import React, { useCallback, useMemo } from 'react';
import type { FC } from 'react';
import { Box, SelectInput } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { isType, lookup, match } from 'variant';
import { CloseScreen } from '../CloseScreen/CloseScreen.js';
import { useBackendComponents } from '../../backend/useBackendComponents.js';
import { useWixCliAppBi } from '../../bi/index.js';
import type {
  BackendViewerComponent,
  BackendViewerComponentNames,
} from './backend-viewer.js';

interface Props {
  backendComponentName: BackendViewerComponentNames;
  onSelect?: (backendComponent: BackendViewerComponent) => void;
  onClose: () => void;
}

export const BackendArrowViewer: FC<Props> = ({
  backendComponentName,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const { backendComponentsByType } = useBackendComponents();

  const getLabelKey = useCallback(
    () =>
      match(
        backendComponentName,
        lookup({
          Api: 'dev_command.backend_apis_screen.select_label',
          Event: 'dev_command.backend_events_screen.select_label',
          ServicePlugin: 'dev_command.backend_plugins_screen.select_label',
        })
      ),
    [backendComponentName]
  );

  const getBackendComponentName = (backendComponent: BackendViewerComponent) =>
    isType(backendComponent, 'ServicePlugin')
      ? backendComponent.servicePlugin.name
      : backendComponent.title;

  const handleSubmit = useCallback(
    (backendComponent: BackendViewerComponent) => {
      bi.cliAction({
        question: t(getLabelKey()),
        questionKey: getLabelKey(),
        action: getBackendComponentName(backendComponent),
        key: 'enter',
      });
      onSelect?.(backendComponent);
    },
    [bi, getLabelKey, onSelect, t]
  );

  const options = useMemo(() => {
    return backendComponentsByType[backendComponentName].map(
      (backendComponent, index) => ({
        key: index.toString(),
        title: getBackendComponentName(backendComponent),
        value: backendComponent,
      })
    );
  }, [backendComponentsByType, backendComponentName]);

  return (
    <Box flexDirection="column" gap={1}>
      <Box flexDirection="column">
        <SelectInput<BackendViewerComponent>
          label={t(getLabelKey())}
          options={options}
          onSubmit={({ value: backendComponent }) =>
            handleSubmit(backendComponent)
          }
        />
      </Box>
      <CloseScreen onClose={onClose} />
    </Box>
  );
};
